import type { Role } from '@/core/auth/types';

/** CUBA sprite symbol ids (without stroke-/fill- prefix) */
export type MenuIcon =
  | 'home'
  | 'user'
  | 'learning'
  | 'calendar'
  | 'support-tickets'
  | 'job-search'
  | 'knowledgebase'
  | 'file'
  | 'ui-kits'
  | 'charts'
  | 'contact'
  | 'task'
  | 'internationalization'
  | 'board'
  | 'project';

export interface MenuLink {
  title: string;
  path: string;
  type: 'link';
  icon?: MenuIcon;
  /** Plan module key; hidden when school plan lacks it. */
  module?: string;
}

export interface MenuSection {
  title: string;
  icon: MenuIcon;
  type: 'sub' | 'link';
  path?: string;
  children?: MenuLink[];
  /** Plan module for the whole section (or use per-child module). */
  module?: string;
}

export interface MenuGroup {
  menutitle: string;
  Items: MenuSection[];
}

export const adminMenu: MenuGroup[] = [
  {
    menutitle: 'School Admin',
    Items: [
      { title: 'Dashboard', icon: 'home', type: 'link', path: '/admin' },
      {
        title: 'Students',
        icon: 'user',
        type: 'sub',
        children: [
          { title: 'All Students', type: 'link', path: '/admin/students' },
          { title: 'Register Student', type: 'link', path: '/admin/students/new' },
          { title: 'Import CSV', type: 'link', path: '/admin/students/import' },
        ],
      },
      {
        title: 'Guardians',
        icon: 'contact',
        type: 'sub',
        children: [
          { title: 'Guardian List', type: 'link', path: '/admin/guardians' },
          { title: 'Import CSV', type: 'link', path: '/admin/guardians/import' },
        ],
      },
      {
        title: 'Academics',
        icon: 'learning',
        type: 'sub',
        children: [
          { title: 'Examinations', type: 'link', path: '/admin/academics/examinations' },
          { title: 'Marks Entry', type: 'link', path: '/admin/academics/marks' },
          { title: 'Subjects', type: 'link', path: '/admin/academics/subjects' },
          { title: 'Report Cards', type: 'link', path: '/admin/academics/report-cards' },
        ],
      },
      {
        title: 'Attendance',
        icon: 'calendar',
        type: 'sub',
        children: [
          { title: 'Daily Capture', type: 'link', path: '/admin/attendance' },
          { title: 'Import CSV', type: 'link', path: '/admin/attendance/import' },
          { title: 'Low Attendance', type: 'link', path: '/admin/attendance/alerts' },
        ],
      },
      {
        title: 'Notifications',
        icon: 'support-tickets',
        type: 'sub',
        children: [
          { title: 'History', type: 'link', path: '/admin/notifications' },
          { title: 'Send', type: 'link', path: '/admin/notifications/send' },
        ],
      },
      {
        title: 'Reports',
        icon: 'file',
        type: 'sub',
        children: [{ title: 'Academic Reports', type: 'link', path: '/admin/reports' }],
      },
      {
        title: 'Settings',
        icon: 'ui-kits',
        type: 'sub',
        children: [
          { title: 'All Users', type: 'link', path: '/admin/users' },
          { title: 'Roles & Permissions', type: 'link', path: '/admin/users/roles' },
          { title: 'Settings Overview', type: 'link', path: '/admin/settings' },
          { title: 'School Profile', type: 'link', path: '/admin/settings/general' },
          { title: 'Grading Systems', type: 'link', path: '/admin/settings/grading' },
        ],
      },
    ],
  },
];

export const parentMenu: MenuGroup[] = [
  {
    menutitle: 'Parent Portal',
    Items: [
      { title: 'Overview', icon: 'home', type: 'link', path: '/parent' },
      { title: 'Child Progress', icon: 'charts', type: 'link', path: '/parent/progress' },
      { title: 'Reports', icon: 'file', type: 'link', path: '/parent/reports' },
      { title: 'Alerts', icon: 'support-tickets', type: 'link', path: '/parent/alerts' },
      {
        title: 'Career Insights',
        icon: 'job-search',
        type: 'link',
        path: '/parent/career',
        module: 'career',
      },
    ],
  },
];

export const studentMenu: MenuGroup[] = [
  {
    menutitle: 'Student App',
    Items: [
      { title: 'Home', icon: 'home', type: 'link', path: '/student' },
      { title: 'Academics', icon: 'charts', type: 'link', path: '/student/academics' },
      { title: 'Career', icon: 'job-search', type: 'link', path: '/student/career', module: 'career' },
      { title: 'Jobs', icon: 'file', type: 'link', path: '/student/jobs', module: 'jobs' },
      {
        title: 'Job profile',
        icon: 'file',
        type: 'link',
        path: '/student/jobs/profile',
        module: 'jobs',
      },
      {
        title: 'My applications',
        icon: 'file',
        type: 'link',
        path: '/student/jobs/applications',
        module: 'jobs',
      },
      { title: 'More', icon: 'ui-kits', type: 'link', path: '/student/more' },
    ],
  },
];

export const counselorMenu: MenuGroup[] = [
  {
    menutitle: 'Counselor Workspace',
    Items: [
      { title: 'Dashboard', icon: 'home', type: 'link', path: '/counselor' },
      { title: 'Students', icon: 'user', type: 'link', path: '/counselor/students' },
      {
        title: 'Career',
        icon: 'job-search',
        type: 'sub',
        module: 'career',
        children: [
          { title: 'Assessments', type: 'link', path: '/counselor/assessments', module: 'career' },
          {
            title: 'Recommendations',
            type: 'link',
            path: '/counselor/recommendations',
            module: 'career',
          },
          { title: 'Career Plans', type: 'link', path: '/counselor/plans', module: 'career' },
          { title: 'Scheduling', type: 'link', path: '/counselor/scheduling', module: 'career' },
        ],
      },
      {
        title: 'University',
        icon: 'internationalization',
        type: 'sub',
        children: [
          {
            title: 'Subject Combinations',
            type: 'link',
            path: '/counselor/university',
            module: 'university',
          },
          {
            title: 'Qualification Gaps',
            type: 'link',
            path: '/counselor/university/gaps',
            module: 'university',
          },
          {
            title: 'Submit Admission',
            type: 'link',
            path: '/counselor/university/admissions',
            module: 'university',
          },
          {
            title: 'Visa Cases',
            type: 'link',
            path: '/counselor/university/visas',
            module: 'visas',
          },
        ],
      },
      {
        title: 'Scholarships',
        icon: 'knowledgebase',
        type: 'sub',
        module: 'scholarships',
        children: [
          {
            title: 'Browse',
            type: 'link',
            path: '/counselor/scholarships',
            module: 'scholarships',
          },
          {
            title: 'Student Matches',
            type: 'link',
            path: '/counselor/scholarships/matches',
            module: 'scholarships',
          },
        ],
      },
      {
        title: 'Jobs',
        icon: 'job-search',
        type: 'sub',
        module: 'jobs',
        children: [
          { title: 'Open Roles', type: 'link', path: '/counselor/jobs', module: 'jobs' },
          {
            title: 'Applications',
            type: 'link',
            path: '/counselor/jobs/applications',
            module: 'jobs',
          },
        ],
      },
      {
        title: 'Reports',
        icon: 'file',
        type: 'sub',
        children: [
          {
            title: 'Career Reports',
            type: 'link',
            path: '/counselor/reports',
            module: 'career',
          },
          {
            title: 'Risk Analysis',
            type: 'link',
            path: '/counselor/reports/risk',
            module: 'career',
          },
        ],
      },
    ],
  },
];

export const universityMenu: MenuGroup[] = [
  {
    menutitle: 'University Portal',
    Items: [
      { title: 'Dashboard', icon: 'home', type: 'link', path: '/university' },
      {
        title: 'Applications',
        icon: 'file',
        type: 'link',
        path: '/university/admissions',
      },
      {
        title: 'Courses',
        icon: 'knowledgebase',
        type: 'link',
        path: '/university/courses',
      },
    ],
  },
];

export const employerMenu: MenuGroup[] = [
  {
    menutitle: 'Employer Portal',
    Items: [
      { title: 'Dashboard', icon: 'home', type: 'link', path: '/employer' },
      {
        title: 'Job Postings',
        icon: 'job-search',
        type: 'link',
        path: '/employer/postings',
      },
      {
        title: 'Applications',
        icon: 'file',
        type: 'link',
        path: '/employer/applications',
      },
    ],
  },
];

export const royalOverseasMenu: MenuGroup[] = [
  {
    menutitle: 'Royal Overseas',
    Items: [
      { title: 'Console', icon: 'home', type: 'link', path: '/royal-overseas' },
      {
        title: 'Schools',
        icon: 'learning',
        type: 'link',
        path: '/royal-overseas/schools',
      },
      {
        title: 'Network',
        icon: 'knowledgebase',
        type: 'sub',
        children: [
          { title: 'Universities', type: 'link', path: '/royal-overseas/universities' },
          { title: 'Agencies', type: 'link', path: '/royal-overseas/agencies' },
        ],
      },
      {
        title: 'Users',
        icon: 'user',
        type: 'sub',
        children: [
          { title: 'All Users', type: 'link', path: '/royal-overseas/users' },
          { title: 'Roles & Permissions', type: 'link', path: '/royal-overseas/users/roles' },
        ],
      },
      {
        title: 'Analytics',
        icon: 'charts',
        type: 'sub',
        children: [
          { title: 'Revenue', type: 'link', path: '/royal-overseas/revenue' },
          { title: 'Assessments', type: 'link', path: '/royal-overseas/assessments' },
          { title: 'System Usage', type: 'link', path: '/royal-overseas/usage' },
        ],
      },
      {
        title: 'Commercial',
        icon: 'task',
        type: 'sub',
        children: [
          { title: 'Plans', type: 'link', path: '/royal-overseas/plans' },
          { title: 'Commissions', type: 'link', path: '/royal-overseas/commissions' },
          { title: 'Feature Flags', type: 'link', path: '/royal-overseas/feature-flags' },
        ],
      },
      { title: 'Locations', icon: 'internationalization', type: 'link', path: '/royal-overseas/locations' },
      { title: 'Currencies', icon: 'internationalization', type: 'link', path: '/royal-overseas/currencies' },
      { title: 'Countries', icon: 'internationalization', type: 'link', path: '/royal-overseas/countries' },
      {
        title: 'Catalogs',
        icon: 'learning',
        type: 'sub',
        children: [
          { title: 'Scholarships (platform)', type: 'link', path: '/royal-overseas/scholarships' },
          { title: 'Job Categories', type: 'link', path: '/royal-overseas/job-categories' },
        ],
      },
      {
        title: 'University Courses',
        icon: 'knowledgebase',
        type: 'sub',
        children: [
          { title: 'Categories', type: 'link', path: '/royal-overseas/course-categories' },
          { title: 'Levels', type: 'link', path: '/royal-overseas/course-levels' },
          { title: 'Courses', type: 'link', path: '/royal-overseas/courses' },
        ],
      },
      { title: 'Jobs Matching', icon: 'job-search', type: 'link', path: '/royal-overseas/jobs-matching' },
      { title: 'System Settings', icon: 'ui-kits', type: 'link', path: '/royal-overseas/settings' },
    ],
  },
];

export function getMenuForRole(role: Role | null): MenuGroup[] {
  switch (role) {
    case 'super_admin':
      return royalOverseasMenu;
    case 'school_admin':
      return adminMenu;
    case 'teacher':
      return adminMenuForTeacher();
    case 'counselor':
      return counselorMenu;
    case 'parent':
      return parentMenu;
    case 'student':
      return studentMenu;
    case 'university':
      return universityMenu;
    case 'employer':
      return employerMenu;
    case 'agent':
      return counselorMenu;
    default:
      return [];
  }
}

/** Hide plan-gated menu items the school is not entitled to. */
export function filterMenuByModules(
  menu: MenuGroup[],
  modules: string[] | null | undefined,
): MenuGroup[] {
  if (modules == null) return menu;
  const allowed = new Set(modules);
  const allowedModule = (mod?: string) => !mod || allowed.has(mod);

  return menu
    .map((group) => ({
      ...group,
      Items: group.Items.map((section) => {
        if (!allowedModule(section.module)) return null;
        if (!section.children?.length) return section;
        const children = section.children.filter((c) => allowedModule(c.module));
        if (!children.length) return null;
        return { ...section, children };
      }).filter((item): item is MenuSection => item != null),
    }))
    .filter((group) => group.Items.length > 0);
}

function adminMenuForTeacher(): MenuGroup[] {
  return adminMenu.map((group) => ({
    ...group,
    menutitle: 'Teacher Portal',
    Items: group.Items.map((item) => {
      if (item.title === 'Students') {
        return {
          title: 'My Students',
          icon: 'user' as const,
          type: 'link' as const,
          path: '/admin/students',
        };
      }
      if (item.title === 'Guardians') {
        return null;
      }
      if (item.title === 'Academics' && item.children) {
        return {
          ...item,
          children: item.children.filter(
            (child) =>
              child.path === '/admin/academics/marks' ||
              child.path === '/admin/academics/report-cards',
          ),
        };
      }
      if (item.title === 'Attendance' && item.children) {
        return {
          ...item,
          children: item.children.filter(
            (child) =>
              child.path === '/admin/attendance' ||
              child.path === '/admin/attendance/alerts',
          ),
        };
      }
      if (item.title === 'Settings' && item.children) {
        return {
          ...item,
          children: item.children.filter(
            (child) =>
              !child.path.startsWith('/admin/users') &&
              child.path !== '/admin/settings/grading',
          ),
        };
      }
      return item;
    }).filter((item): item is NonNullable<typeof item> => item != null),
  }));
}
